/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Exocet Header Scroll Motion — GSAP ScrollTrigger scroll behaviors
 */
(function () {
  "use strict";

  var SCROLLED_THRESHOLD = 0.92;
  var MORPH_DISTANCE = 120;
  var SHRINK_DISTANCE = 80;
  var HIDE_DELTA = 8;
  var HIDE_TOP = 100;
  var SCRUB_LAG = 0.45;

  function supports() {
    return (
      typeof gsap !== "undefined" &&
      typeof ScrollTrigger !== "undefined" &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
  }

  function parsePx(value, fallback) {
    var n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function setProgress(p) {
    document.documentElement.style.setProperty("--header-scroll-p", String(clamp(p, 0, 1)));
  }

  function findHeroTrigger() {
    return document.querySelector(".home-hero, .product-hero, .collection-hero, [data-header-hero-trigger]");
  }

  function createHeaderScrollMotion(config) {
    var header = config.header;
    var wrapper = config.wrapper;
    var spacer = config.spacer;
    var announcementBar = config.announcementBar;
    var announcementSpacer = config.announcementSpacer;
    var scrollBehavior = config.scrollBehavior;
    var isTransparent = config.isTransparent;
    var isGlassPill = config.isGlassPill;
    var getMegamenuActive =
      config.getMegamenuActive ||
      function () {
        return null;
      };

    var triggers = [];
    var hideTween = null;
    var hidden = false;
    var lastScrollY = window.scrollY;
    var headerHeight = function () {
      return parsePx(getComputedStyle(header).getPropertyValue("--header-height"), 72);
    };

    function announcementDismissed() {
      return (
        announcementBar &&
        (announcementBar.classList.contains("is-dismissed") || announcementBar.classList.contains("is-removed"))
      );
    }

    function applyScrolledClass(p) {
      var scrolled = p >= SCROLLED_THRESHOLD;
      header.classList.toggle("is-scrolled", scrolled);
      if (wrapper) wrapper.classList.toggle("is-scrolled", scrolled);
      if (spacer && isGlassPill) {
        spacer.setAttribute("data-glass-mode", scrolled ? "scrolled" : "static");
      }
    }

    function applyAnnouncement(p) {
      if (!announcementBar || announcementDismissed()) return;

      var fade = Math.min(1, p * 2.4);
      gsap.set(announcementBar, {
        opacity: 1 - fade,
        pointerEvents: fade > 0.9 ? "none" : "auto",
      });
      if (announcementSpacer) {
        gsap.set(announcementSpacer, { opacity: 1 - fade });
      }

      if (p >= SCROLLED_THRESHOLD) {
        announcementBar.classList.add("is-hidden");
        if (announcementSpacer) announcementSpacer.style.display = "none";
      } else {
        announcementBar.classList.remove("is-hidden");
        if (announcementSpacer) announcementSpacer.style.display = "";
      }
    }

    function updateProgress(p) {
      setProgress(p);
      applyScrolledClass(p);
      if (scrollBehavior === "glass_fullpage") applyAnnouncement(p);
    }

    function morphTriggerConfig() {
      var hero = findHeroTrigger();
      var distance = scrollBehavior === "sticky_shrink" ? SHRINK_DISTANCE : MORPH_DISTANCE;

      if (hero && (isTransparent || scrollBehavior === "glass_fullpage")) {
        return {
          trigger: hero,
          start: "top top",
          end: "bottom top+=" + headerHeight(),
          scrub: SCRUB_LAG,
          invalidateOnRefresh: true,
        };
      }

      return {
        trigger: document.documentElement,
        start: "top top",
        end: "+=" + distance,
        scrub: SCRUB_LAG,
        invalidateOnRefresh: true,
      };
    }

    function setupMorphScrub() {
      var stConfig = morphTriggerConfig();
      stConfig.onUpdate = function (self) {
        updateProgress(self.progress);
      };
      triggers.push(ScrollTrigger.create(stConfig));
    }

    function showHeader() {
      if (!hidden) return;
      hidden = false;
      header.classList.remove("is-hidden");
      if (wrapper) wrapper.classList.remove("is-hidden");
      if (hideTween) hideTween.kill();
      hideTween = gsap.to(wrapper || header, {
        yPercent: 0,
        duration: 0.42,
        ease: "power3.out",
        overwrite: "auto",
      });
    }

    function hideHeader() {
      if (hidden) return;
      hidden = true;
      header.classList.add("is-hidden");
      if (wrapper) wrapper.classList.add("is-hidden");
      if (hideTween) hideTween.kill();
      hideTween = gsap.to(wrapper || header, {
        yPercent: -100,
        duration: 0.36,
        ease: "power3.inOut",
        overwrite: "auto",
      });
    }

    function setupHideShow() {
      if (isTransparent) setupMorphScrub();

      triggers.push(
        ScrollTrigger.create({
          trigger: document.documentElement,
          start: "top top",
          end: "max",
          onUpdate: function (self) {
            var current = self.scroll();
            var delta = current - lastScrollY;

            if (current <= HIDE_TOP || getMegamenuActive()) {
              showHeader();
              if (Math.abs(delta) > HIDE_DELTA) lastScrollY = current;
              return;
            }

            if (delta > HIDE_DELTA) {
              hideHeader();
              lastScrollY = current;
            } else if (delta < -HIDE_DELTA) {
              showHeader();
              lastScrollY = current;
            }
          },
        }),
      );
    }

    gsap.set(wrapper || header, { yPercent: 0 });
    document.documentElement.classList.add("header-scroll-motion-ready");
    setProgress(0);

    var needsMorph =
      scrollBehavior === "glass_fullpage" ||
      scrollBehavior === "sticky_shrink" ||
      (isTransparent && scrollBehavior !== "hide_show");

    if (scrollBehavior === "hide_show") {
      setupHideShow();
    } else if (needsMorph) {
      setupMorphScrub();
    }

    // ScrollTrigger.refresh() removed here; the window-load refresh in
    // exocet-motion.js fires after images decode and is the single source.

    if (needsMorph && triggers.length) {
      updateProgress(triggers[0].progress);
    } else if (window.scrollY > 60 && isTransparent) {
      updateProgress(1);
    }

    return {
      destroy: function () {
        triggers.forEach(function (st) {
          st.kill();
        });
        triggers = [];
        if (hideTween) hideTween.kill();
        document.documentElement.classList.remove("header-scroll-motion-ready");
        document.documentElement.style.removeProperty("--header-scroll-p");
        header.classList.remove("is-scrolled", "is-hidden");
        if (wrapper) {
          wrapper.classList.remove("is-scrolled", "is-hidden");
          gsap.set(wrapper, { clearProps: "transform" });
        }
      },
      refresh: function () {
        ScrollTrigger.refresh();
        if (needsMorph && triggers.length) updateProgress(triggers[0].progress);
      },
      showHeader: showHeader,
    };
  }

  window.ExocetHeaderScrollMotion = {
    supports: supports,
    create: createHeaderScrollMotion,
  };
})();
